import React from 'react';
import { css } from '@emotion/react';
import { Button, color, spacing, typography } from '@uniquegood/realworld-studio-design';
import { onMobile } from '@/styles/responsive';
import { useIsMobile, useRequest } from '@/hooks';

// TODO: css 리팩토링

interface CreateProjectTemplateModalProps {
  onUpgradePlanClick?: () => void;
  onClickManualCreate?: (e: React.MouseEvent) => void;
  onClickAiCreate?: () => void;
  onClose?: () => void;
  isInsufficientCredit?: boolean;
  plan?: PlanType;
}

function CreateProjectTemplateModal({
  onUpgradePlanClick,
  onClickManualCreate,
  onClickAiCreate,
  onClose,
  isInsufficientCredit = false,
  plan = 'Free'
}: CreateProjectTemplateModalProps) {
  const isMobile = useIsMobile();

  // AI 제품 가격 정보 가져오기
  const { data: aiProductData } =
    useRequest<CommonResponseModel<AiProductResponseModel[]>>(`/api/products/ai`);

  // Template 가격 계산 (기본값 40)
  const templateCreditPrice =
    aiProductData?.data.find((product) => product.generateType === 'Template')?.creditPrice ?? 40;
  return (
    <div css={getContainerStyle(isMobile)}>
      <button type="button" css={CloseIconStyle} onClick={onClose} aria-label="닫기">
        <img width="20px" height="20px" src="/assets/icons/close-x.svg" alt="close icon" />
      </button>
      <h1 css={TitleStyle}>
        <span css={DesktopTitleStyle}>어떤 방법으로 콘텐츠를 만들까요?</span>
        <span css={MobileTitleStyle}>
          어떤 방법으로
          <br />
          콘텐츠를 만들까요?
        </span>
      </h1>

      <div css={getCardsContainerStyle(isMobile)}>
        {/* 처음부터 만들기 박스 */}
        <button
          type="button"
          css={[CardStyle, ManualCardStyle]}
          onClick={onClickManualCreate}
          onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClickManualCreate?.(e as unknown as React.MouseEvent);
            }
          }}
          aria-label="처음부터 만들기"
        >
          <div css={CardContentStyle}>
            <h2 css={CardTitleStyle}>처음부터 만들기</h2>
            <p css={CardDescriptionStyle}>
              원하는 템플릿을 선택하여
              <br />
              직접 만들어요
            </p>
          </div>
        </button>

        {/* AI로 만들기 박스 */}
        {isInsufficientCredit && (plan === 'Free' || plan === 'Pro' || plan === 'Teacher') ? (
          <div css={[DisabledCardStyle, AiCardDisabledStyle]} aria-label="AI로 만들기">
            {/* AI Beta 라벨 */}
            <div css={AiBetaLabelDisabledStyle}>
              <img src="/assets/icons/stars.svg" alt="stars icon" css={AiBetaIconDisabledStyle} />
              <span css={AiBetaTextDisabledStyle}>AI Beta</span>
            </div>

            <div css={[CardContentStyle, AiCardContentStyle]}>
              <h2 css={CardTitleDisabledStyle}>AI로 만들기</h2>
              <p css={CardDescriptionDisabledStyle}>
                스토리, 이미지, BGM을
                <br />
                자동으로 만들어줘요
              </p>
            </div>
            {/* 가격 정보 */}
            <div css={PriceInfoDisabledStyle}>
              <div css={PriceItemStyle}>
                <img src="/assets/icons/stars.svg" alt="stars icon" css={PriceIconCriticalStyle} />
                <span css={PriceTextCriticalStyle}>{templateCreditPrice}</span>
                <span css={InsufficientCreditTextStyle}>(크레딧이 부족해요)</span>
              </div>
            </div>
            {/* 요금제 가입하고 크레딧 받기 버튼 */}
            <Button
              type="primary"
              size="medium"
              cssStyle={SubscriptionButtonStyle}
              onClick={() => {
                onUpgradePlanClick?.();
              }}
            >
              요금제 가입하고 추가 크레딧 받기
            </Button>
          </div>
        ) : (
          <button
            type="button"
            css={[CardStyle, AiCardStyle]}
            onClick={onClickAiCreate}
            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onClickAiCreate?.();
              }
            }}
            aria-label="AI로 만들기"
          >
            {/* AI Beta 라벨 */}
            <div css={AiBetaLabelStyle}>
              <img src="/assets/icons/stars.svg" alt="stars icon" css={AiBetaIconStyle} />
              <span css={AiBetaTextStyle}>AI Beta</span>
            </div>

            <div css={[CardContentStyle, AiCardContentStyle]}>
              <h2 css={CardTitleStyle}>AI로 만들기</h2>
              <p css={CardDescriptionStyle}>
                스토리, 이미지, BGM을
                <br />
                자동으로 만들어줘요
              </p>
            </div>
            {/* 가격 정보 */}
            <div css={PriceInfoStyle}>
              <div css={PriceItemStyle}>
                <img src="/assets/icons/stars.svg" alt="stars icon" css={PriceIconStyle} />
                <span css={PriceTextStyle}>{templateCreditPrice}</span>
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

export default CreateProjectTemplateModal;

// 컨테이너 스타일
const getContainerStyle = (isMobile: boolean) => css`
  background-color: ${color.surface_default_default};
  border-radius: ${spacing.borderRadius.xlarge};
  padding: ${isMobile ? '24px 16px' : '16px'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: ${isMobile ? spacing.margin.medium : '40px'};
  width: 100%;
  box-sizing: border-box;
  position: relative;
  ${isMobile && 'max-width: 375px;'}
`;

// 타이틀 스타일
const TitleStyle = css`
  font-family: 'Pretendard';
  font-weight: 700;
  font-size: ${typography.size.large};
  line-height: 32px;
  color: #0e1017;
  text-align: center;
  margin: 0;

  ${onMobile} {
    font-size: 24px;
    line-height: 28px;
  }
`;

// 데스크톱 타이틀 스타일
const DesktopTitleStyle = css`
  display: block;
  white-space: nowrap;

  ${onMobile} {
    display: none;
  }
`;

// 모바일 타이틀 스타일
const MobileTitleStyle = css`
  display: none;

  ${onMobile} {
    display: block;
    line-height: 28px;
  }
`;

// 카드 컨테이너 스타일
const getCardsContainerStyle = (isMobile: boolean) => css`
  display: flex;
  flex-direction: ${isMobile ? 'column' : 'row'};
  gap: ${isMobile ? spacing.margin.medium : spacing.margin.large};
  width: ${isMobile ? '100%' : '592px'};

  ${onMobile} {
    height: auto;
  }
`;

// 기본 카드 스타일
const CardStyle = css`
  background-color: ${color.surface_default_default};
  border-radius: 20px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 284px;
  cursor: pointer;
  box-sizing: border-box;
  transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;

  ${onMobile} {
    width: 100%;
    height: 192px;
  }
`;

const DisabledCardStyle = css`
  background-color: ${color.surface_default_default};
  border-radius: 20px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 284px;
  cursor: pointer;
  box-sizing: border-box;

  ${onMobile} {
    width: 100%;
  }
`;

// 수동 생성 카드 스타일
const ManualCardStyle = css`
  box-shadow: 0px 0px 32px 0px rgba(0, 0, 0, 0.1);
  border: none;
  background: none;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0px 0px 32px 0px rgba(0, 0, 0, 0.2);
  }
`;

// AI 생성 카드 스타일
const AiCardStyle = css`
  box-shadow: 0px 0px 32px 0px rgba(65, 91, 255, 0.25);
  gap: 16px;
  border: none;
  background: none;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0px 0px 32px 0px rgba(65, 91, 255, 0.4);
  }
`;

// 카드 콘텐츠 스타일
const CardContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
`;

// AI 카드 콘텐츠 스타일
const AiCardContentStyle = css`
  margin-top: 8px;
`;

// 카드 타이틀 스타일
const CardTitleStyle = css`
  font-family: 'Pretendard';
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: #0e1017;
  margin: 0;
  text-align: center;

  ${onMobile} {
    font-size: 16px;
    line-height: 24px;
  }
`;

// 카드 설명 스타일
const CardDescriptionStyle = css`
  font-family: 'Pretendard';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #797d88;
  margin: 0;
  text-align: center;
`;

// AI Beta 라벨 스타일
const AiBetaLabelStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 16px;
  background: linear-gradient(94deg, #4a41ff 0%, #22f8e2 100%);
  backdrop-filter: blur(8px);
`;

// AI Beta 텍스트 스타일
const AiBetaTextStyle = css`
  font-family: 'Pretendard';
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${color.text_white};
  text-shadow: rgba(249, 240, 255, 0.4) 0px 0px 4px;
`;

// 가격 정보 스타일
const PriceInfoStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

// 가격 텍스트 스타일
const PriceTextStyle = css`
  font-family: 'Pretendard';
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #a9a9a9;
  text-align: center;
`;

// AI Beta 아이콘 스타일
const AiBetaIconStyle = css`
  width: 12px;
  height: 12px;
  filter: brightness(0) invert(1); /* SVG를 흰색으로 변경 */
`;

// 가격 정보 아이콘 스타일
const PriceIconStyle = css`
  width: 16px;
  height: 16px;
  filter: brightness(0) saturate(100%) invert(71%) sepia(0%) saturate(1%) hue-rotate(191deg)
    brightness(95%) contrast(91%); /* SVG를 #A9A9A9 색상으로 변경 */
`;

const CloseIconStyle = css`
  width: 40px;
  height: 40px;
  position: absolute;
  top: -8px;
  right: -8px;
  cursor: pointer;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;

  ${onMobile} {
    top: 26px;
    right: 26px;
  }
`;

// 크레딧 부족 상태 스타일들
const AiBetaLabelDisabledStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 16px;
  background: var(--surface-surface-disabled, #e8e8e8);
`;

const AiBetaTextDisabledStyle = css`
  font-family: 'Pretendard';
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #adadad;
  text-shadow: rgba(249, 240, 255, 0.4) 0px 0px 4px;
`;

const AiBetaIconDisabledStyle = css`
  width: 12px;
  height: 12px;
  filter: brightness(0) saturate(100%) invert(74%) sepia(0%) saturate(0%) hue-rotate(191deg)
    brightness(98%) contrast(89%); /* SVG를 #bdbdbd 색상으로 변경 */
`;

// 크레딧 부족 상태 - AI 카드 스타일
const AiCardDisabledStyle = css`
  background-color: #f5f5f5;
  gap: 16px;
  border: none;

  // &:hover {
  //   transform: scale(1.05);
  //   box-shadow: 0px 0px 32px 0px rgba(0, 0, 0, 0.2);
  // }
`;

// 크레딧 부족 상태 - 카드 타이틀 스타일
const CardTitleDisabledStyle = css`
  font-family: 'Pretendard';
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: #adadad;
  margin: 0;
  text-align: center;

  ${onMobile} {
    font-size: 16px;
    line-height: 24px;
  }
`;

// 크레딧 부족 상태 - 카드 설명 스타일
const CardDescriptionDisabledStyle = css`
  font-family: 'Pretendard';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #adadad;
  margin: 0;
  text-align: center;
`;

// 크레딧 부족 상태 - 가격 정보 컨테이너 스타일
const PriceInfoDisabledStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

// 가격 아이템 스타일 (아이콘 + 숫자)
const PriceItemStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

// 크레딧 부족 상태 - 가격 아이콘 스타일 (빨간색)
const PriceIconCriticalStyle = css`
  width: 16px;
  height: 16px;
  filter: brightness(0) saturate(100%) invert(26%) sepia(89%) saturate(1583%) hue-rotate(346deg)
    brightness(91%) contrast(95%); /* SVG를 #d53d40 색상으로 변경 */
`;

// 크레딧 부족 상태 - 가격 텍스트 스타일 (빨간색)
const PriceTextCriticalStyle = css`
  font-family: 'Pretendard';
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #d53d40;
  text-align: center;
`;

// 크레딧 부족 안내 텍스트 스타일
const InsufficientCreditTextStyle = css`
  font-family: 'Pretendard';
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #d53d40;
  text-align: center;
`;

// 요금제 가입하고 크레딧 받기 버튼 스타일
const SubscriptionButtonStyle = css`
  border: none;
  background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);

  &:hover {
    background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
    transform: scale(1.05);
    opacity: 0.9;
  }
`;
